import { Icon } from '@iconify/react';
import { TextBox } from '@progress/kendo-react-inputs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { UploadFileInfo, UploadListItemProps } from '@progress/kendo-react-upload';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import {
  PageRangeValidationError,
  PdfFileMeta,
  sanitizePageRangeInput,
} from 'src/utils/fileValidation';

const fileTypeIcons: { [key: string]: string } = {
  exe: 'k-i-file-config',
  pdf: 'k-i-file-pdf',
  jpg: 'k-i-file-image',
  jpeg: 'k-i-file-image',
  png: 'k-i-file-image',
  gif: 'k-i-file-image',
  bmp: 'k-i-file-image',
  doc: 'k-i-file-word',
  docx: 'k-i-file-word',
  xls: 'k-i-file-data',
  xlsx: 'k-i-file-data',
  txt: 'k-i-file-txt',
  default: 'k-i-file',
};

export interface UploadFileListItemProps extends UploadListItemProps {
  showScanByPage?: boolean;
  pdfMeta?: Record<string, PdfFileMeta>;
  onConfirmRange?: (uid: string, draftRange: string) => void;
  onDraftRangeChange?: (uid: string, draftRange: string) => void;
  onClearRange?: (uid: string) => void;
  onEditRange?: (uid: string) => void;
  onScanByPageClick?: (uid: string, file: UploadFileInfo) => void;
  onTogglePanel?: (uid: string, open: boolean) => void;
  selectedUid?: string | null;
  onSelectFile?: (uid: string) => void;
}

const stopUploadListItemEvent = (event: MouseEvent<HTMLElement> | PointerEvent<HTMLElement>) => {
  event.stopPropagation();
};

/** Keep the click from being lost when another row's page-range input blurs/re-renders. */
const preventBlurSteal = (event: MouseEvent<HTMLElement> | PointerEvent<HTMLElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

const focusPageRangeInput = (container: HTMLElement | null) => {
  const input = container?.querySelector('input');
  if (input instanceof HTMLInputElement) {
    window.setTimeout(() => input.focus(), 0);
  }
};

const handleScanByPageInteraction = (
  event: MouseEvent<HTMLElement> | PointerEvent<HTMLElement>
) => {
  stopUploadListItemEvent(event);
  focusPageRangeInput(event.currentTarget);
};

const formatValidationMessage = (
  t: (key: string, options?: Record<string, string | number>) => string,
  error: PageRangeValidationError
): string => {
  if (!error) {
    return '';
  }
  if (error.key === 'scanByPage.pageExceedsCount') {
    return t(error.key, { input: error.input, total: error.total });
  }
  if (error.key === 'scanByPage.invalidPageNumber') {
    return t(error.key, { page: error.page, total: error.total });
  }
  if (error.key === 'scanByPage.duplicatePageNumber') {
    return t(error.key, { page: error.page });
  }
  return t(error.key);
};

const ScanByPageInputPanel = ({
  uid,
  meta,
  onConfirmRange,
  onDraftRangeChange,
  onClearRange,
  onTogglePanel,
}: {
  uid: string;
  meta: PdfFileMeta;
  onConfirmRange?: (uid: string, draftRange: string) => void;
  onDraftRangeChange?: (uid: string, draftRange: string) => void;
  onClearRange?: (uid: string) => void;
  onTogglePanel?: (uid: string, open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { pageCount } = meta;
  const [draftRange, setDraftRange] = useState(meta.draftRange);

  useEffect(() => {
    setDraftRange(meta.draftRange);
  }, [meta.draftRange, meta.isPanelOpen, uid]);

  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (meta.isPanelOpen) {
      focusPageRangeInput(panelRef.current);
    }
  }, [meta.isPanelOpen, uid]);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const nextFocus = event.relatedTarget as Node | null;
      if (panelRef.current?.contains(nextFocus)) {
        return;
      }

      window.setTimeout(() => {
        const trimmed = draftRange.trim();
        if (!trimmed) {
          onClearRange?.(uid);
          return;
        }
        onConfirmRange?.(uid, draftRange);
      }, 0);
    },
    [draftRange, onClearRange, onConfirmRange, uid]
  );

  if (pageCount == null) {
    return null;
  }

  const showError = !!meta.validationError;
  const validationMessage = showError ? formatValidationMessage(t, meta.validationError) : '';
  const pageCountTitle = t('scanByPage.pageCount', { count: pageCount });
  const pageCountLabel = t('scanByPage.totalPages', { count: pageCount });

  return (
    <div
      ref={panelRef}
      className="flex items-center justify-end"
      onMouseDown={handleScanByPageInteraction}
      onPointerDown={handleScanByPageInteraction}
      onClick={stopUploadListItemEvent}
    >
      <Tooltip anchorElement="target" position="top" parentTitle>
        <div className="flex items-center gap-2">
          {showError && (
            <span
              title={validationMessage}
              className="flex shrink-0 items-center text-red-600"
              data-testid="scan-by-page-error-icon"
            >
              <Icon icon="lucide:circle-alert" width={16} height={16} />
            </span>
          )}
          <TextBox
            value={draftRange}
            placeholder={t('scanByPage.hint')}
            valid={!showError}
            onChange={(event) => {
              const nextValue = sanitizePageRangeInput(String(event.value ?? ''));
              setDraftRange(nextValue);
              onDraftRangeChange?.(uid, nextValue);
            }}
            onBlur={handleBlur}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                onTogglePanel?.(uid, false);
              }
            }}
            width={90}
            size="small"
          />
          <span
            className="shrink-0 whitespace-nowrap text-xs text-gray-500"
            title={pageCountTitle}
            data-testid="scan-by-page-page-count"
          >
            {pageCountLabel}
          </span>
        </div>
      </Tooltip>
    </div>
  );
};

const ConfirmedRangeTag = ({
  uid,
  range,
  editable = true,
  onEditRange,
}: {
  uid: string;
  range: string;
  editable?: boolean;
  onEditRange?: (uid: string) => void;
}) => {
  const { t } = useTranslation();

  if (!editable) {
    return (
      <span
        className="inline-flex h-[30px] shrink-0 items-center rounded bg-info px-3 text-[13px] text-white"
        data-testid="scan-by-page-confirmed-range"
        aria-disabled="true"
      >
        {t('scanByPage.pages', { range })}
      </span>
    );
  }

  return (
    <Button
      type="button"
      color="info"
      size="md"
      className="!m-0 shrink-0 !px-3"
      data-testid="scan-by-page-confirmed-range"
      onClick={(event) => {
        event.stopPropagation();
        onEditRange?.(uid);
      }}
      onMouseDown={preventBlurSteal}
      onPointerDown={preventBlurSteal}
    >
      {t('scanByPage.pages', { range })}
    </Button>
  );
};

const ScanByPageErrorMessage = ({ message }: { message: string }) => (
  <div
    className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-red-600"
    data-testid="scan-by-page-file-error"
  >
    <Icon icon="lucide:circle-alert" className="shrink-0" width={16} height={16} />
    <span>{message}</span>
  </div>
);

const ScanByPageInline = ({
  uid,
  file,
  meta,
  onConfirmRange,
  onDraftRangeChange,
  onClearRange,
  onEditRange,
  onScanByPageClick,
  onTogglePanel,
  onSelectFile,
}: {
  uid: string;
  file: UploadFileInfo;
  meta?: PdfFileMeta;
  onConfirmRange?: (uid: string, draftRange: string) => void;
  onDraftRangeChange?: (uid: string, draftRange: string) => void;
  onClearRange?: (uid: string) => void;
  onEditRange?: (uid: string) => void;
  onScanByPageClick?: (uid: string, file: UploadFileInfo) => void;
  onTogglePanel?: (uid: string, open: boolean) => void;
  onSelectFile?: (uid: string) => void;
}) => {
  const { t } = useTranslation();

  const isUninspected =
    !meta ||
    (meta.pageCount == null && !meta.isLoading && !meta.error && meta.confirmedRange == null);

  const handleScanByPageClick = () => {
    onSelectFile?.(uid);
    onScanByPageClick?.(uid, file);
  };

  if (isUninspected) {
    return (
      <Button
        color="info"
        size="md"
        className="!m-0 shrink-0"
        onMouseDown={preventBlurSteal}
        onPointerDown={preventBlurSteal}
        onClick={(event) => {
          event.stopPropagation();
          handleScanByPageClick();
        }}
      >
        {t('scanByPage')}
      </Button>
    );
  }

  if (meta.isLoading) {
    return (
      <div className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-600">
        <Icon icon="lucide:loader-2" className="animate-spin shrink-0" width={16} height={16} />
        <span>{t('scanByPage.loading')}</span>
      </div>
    );
  }

  if (meta.error === 'password-protected') {
    return <ScanByPageErrorMessage message={t('scanByPage.passwordProtected')} />;
  }

  if (meta.error === 'corrupt') {
    return <ScanByPageErrorMessage message={t('scanByPage.corruptFile')} />;
  }

  const confirmedRange = meta.confirmedRange ?? (meta.pageCount === 1 ? '1' : null);
  const isSinglePage = meta.pageCount === 1;

  // Single-page PDFs only show a non-editable tag — never open the range input.
  if (confirmedRange && (!meta.isPanelOpen || isSinglePage)) {
    return (
      <ConfirmedRangeTag
        uid={uid}
        range={confirmedRange}
        editable={!isSinglePage}
        onEditRange={onEditRange}
      />
    );
  }

  if (!meta.isPanelOpen) {
    return (
      <Button
        color="info"
        size="md"
        className="!m-0 shrink-0"
        onMouseDown={preventBlurSteal}
        onPointerDown={preventBlurSteal}
        onClick={(event) => {
          event.stopPropagation();
          handleScanByPageClick();
        }}
      >
        {t('scanByPage')}
      </Button>
    );
  }

  return (
    <ScanByPageInputPanel
      uid={uid}
      meta={meta}
      onConfirmRange={onConfirmRange}
      onDraftRangeChange={onDraftRangeChange}
      onClearRange={onClearRange}
      onTogglePanel={onTogglePanel}
    />
  );
};

const UploadFileListItem = ({
  showScanByPage = false,
  pdfMeta = {},
  onConfirmRange,
  onDraftRangeChange,
  onClearRange,
  onEditRange,
  onScanByPageClick,
  onTogglePanel,
  selectedUid = null,
  onSelectFile,
  ...props
}: UploadFileListItemProps) => {
  const { t } = useTranslation();
  const isPdf = useCallback((extension?: string) => extension?.toLowerCase() === '.pdf', []);

  return (
    <ul className="k-reset w-full h-full overflow-y-auto">
      {props.files.map((file) => {
        const fileExtension = file.extension?.toLowerCase().replace('.', '') || 'default';
        const iconClass = fileTypeIcons[fileExtension] || fileTypeIcons.default;
        const meta = pdfMeta[file.uid];
        const showPanel = showScanByPage && isPdf(file.extension);
        const uniformRowClass = showScanByPage ? '!h-[62px] !items-center' : '!items-center';
        const isSelected = selectedUid === file.uid;

        return (
          <li
            key={file.uid}
            data-uid={file.uid}
            data-selected={isSelected}
            className={isSelected ? 'relative z-[1] mb-0.5' : undefined}
          >
            <div
              className={`k-file-single !flex cursor-pointer ${uniformRowClass} ${
                isSelected ? '!rounded !border-2 !border-solid !border-[#0E80B4] !bg-[#e8f4fc]' : ''
              }`}
              onClick={(event) => {
                // Kendo's own <li> wrapper listens for this click to set its internal
                // navigationIndex; once set, it force-focuses that <li> on every re-render
                // (see UploadListGroup.componentDidUpdate), stealing focus from any input
                // inside this row (e.g. mid-keystroke in the scan-by-page range field).
                event.stopPropagation();
                onSelectFile?.(file.uid);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  event.stopPropagation();
                  onSelectFile?.(file.uid);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="k-progressbar">
                <span className="k-progress" />
              </div>
              <span className="k-file-group-wrapper shrink-0 self-center">
                <span className={`k-file-group k-icon ${iconClass}`} />
                <span className="k-file-state" />
              </span>
              <span className="k-file-name-size-wrapper flex min-w-0 flex-1 flex-col justify-center self-center">
                <span className="k-file-name" title={file.name}>
                  {file.name}
                </span>
                <span className="k-file-size">{(Number(file.size) / 1024).toFixed(2)} KB</span>
              </span>
              {showPanel && (
                <div
                  className="mx-2 flex shrink-0 items-center justify-end self-center"
                  onMouseDown={stopUploadListItemEvent}
                  onPointerDown={stopUploadListItemEvent}
                  onClick={stopUploadListItemEvent}
                >
                  <ScanByPageInline
                    uid={file.uid}
                    file={file}
                    meta={meta}
                    onConfirmRange={onConfirmRange}
                    onDraftRangeChange={onDraftRangeChange}
                    onClearRange={onClearRange}
                    onEditRange={onEditRange}
                    onScanByPageClick={onScanByPageClick}
                    onTogglePanel={onTogglePanel}
                    onSelectFile={onSelectFile}
                  />
                </div>
              )}
              <strong className="k-upload-status mr-2 shrink-0 self-center">
                <Tooltip anchorElement="target" position="bottom" parentTitle>
                  <button
                    onMouseDown={preventBlurSteal}
                    onPointerDown={preventBlurSteal}
                    onClick={(event) => {
                      event.stopPropagation();
                      props.onRemove(file.uid);
                    }}
                    type="button"
                    title={t('scanByPage.removeFile')}
                    aria-label={t('scanByPage.removeFile')}
                    className="k-button k-button-icon k-flat border-transparent focus:ring focus:ring-black"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        props.onRemove(file.uid);
                      }
                    }}
                  >
                    <span className="k-icon k-delete k-i-x focus:border focus:border-black" />
                  </button>
                </Tooltip>
              </strong>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default UploadFileListItem;
